import { create } from 'zustand';
import api from '../lib/api';
import type {
  MatchReviewRow,
  MatchingPagination,
  MatchingFilters,
  MatchingSort,
} from '../types/matching';

const PAGE_SIZE = 25;

interface MatchingState {
  rows: MatchReviewRow[];
  pagination: MatchingPagination;
  stats: { possibleDuplicate: number };
  filters: MatchingFilters;
  expandedRowId: string | null;
  isLoading: boolean;
  error: string | null;
  actionInFlight: Set<string>;

  fetchQueue: (page?: number) => Promise<void>;
  fetchStats: () => Promise<void>;
  setPage: (page: number) => void;
  setFilters: (patch: Partial<MatchingFilters>) => void;
  expandRow: (id: string | null) => void;
  confirmDistinct: (id: string) => Promise<void>;
  confirmDuplicate: (id: string) => Promise<void>;
  skip: (id: string) => void;
}

function nextRowIdAfter(rows: MatchReviewRow[], removedId: string): string | null {
  const idx = rows.findIndex((r) => r.id === removedId);
  if (idx < 0) return null;
  const next = rows[idx + 1];
  return next ? next.id : null;
}

export const useMatchingStore = create<MatchingState>((set, get) => ({
  rows: [],
  pagination: { total: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 0 },
  stats: { possibleDuplicate: 0 },
  filters: { sortBy: 'confidence' },
  expandedRowId: null,
  isLoading: false,
  error: null,
  actionInFlight: new Set<string>(),

  fetchQueue: async (page) => {
    try {
      set({ isLoading: true, error: null });
      const { pagination, filters } = get();
      const targetPage = page ?? pagination.page;
      const params = new URLSearchParams();
      params.set('status', 'POSSIBLE_DUPLICATE');
      params.set('page', String(targetPage));
      params.set('pageSize', String(PAGE_SIZE));
      if (filters.state) params.set('state', filters.state);
      // Note: backend already orders by confidence DESC, similarityScore DESC.
      // The sortBy filter here is a client-side preference; for V1.0 we just
      // accept the backend's order and don't override.

      const res = await api.get(`/api/admin/matching/queue?${params.toString()}`);
      const fetchedRows: MatchReviewRow[] = res.data.rows;
      set({
        rows: fetchedRows,
        pagination: res.data.pagination,
        isLoading: false,
        expandedRowId: fetchedRows.length > 0 ? fetchedRows[0].id : null,
      });
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      set({
        isLoading: false,
        error: e.response?.data?.message ?? 'Failed to load queue',
      });
    }
  },

  fetchStats: async () => {
    try {
      const res = await api.get('/api/admin/matching/stats');
      const stats = res.data.stats as Record<string, number>;
      set({ stats: { possibleDuplicate: stats.POSSIBLE_DUPLICATE ?? 0 } });
    } catch {
      // non-critical — header count just shows 0 if stats fail
    }
  },

  setPage: (page) => {
    set((s) => ({ pagination: { ...s.pagination, page } }));
    void get().fetchQueue(page);
  },

  setFilters: (patch) => {
    set((s) => ({ filters: { ...s.filters, ...patch } }));
    void get().fetchQueue(1);
  },

  expandRow: (id) => set({ expandedRowId: id }),

  confirmDistinct: async (id) => {
    const inFlight = new Set(get().actionInFlight);
    inFlight.add(id);
    set({ actionInFlight: inFlight });
    try {
      await api.post(`/api/admin/matching/${id}/confirm-distinct`);
      const { rows, stats, pagination } = get();
      const nextExpanded = nextRowIdAfter(rows, id);
      const remaining = rows.filter((r) => r.id !== id);
      set({
        rows: remaining,
        expandedRowId: nextExpanded,
        stats: { possibleDuplicate: Math.max(0, stats.possibleDuplicate - 1) },
        pagination: { ...pagination, total: Math.max(0, pagination.total - 1) },
      });
      if (remaining.length === 0) {
        const newTotal = Math.max(0, pagination.total - 1);
        if (newTotal > 0) {
          const newTotalPages = Math.max(1, Math.ceil(newTotal / pagination.pageSize));
          const targetPage = Math.min(pagination.page, newTotalPages);
          await get().fetchQueue(targetPage);
        }
      }
    } finally {
      const after = new Set(get().actionInFlight);
      after.delete(id);
      set({ actionInFlight: after });
    }
  },

  confirmDuplicate: async (id) => {
    const inFlight = new Set(get().actionInFlight);
    inFlight.add(id);
    set({ actionInFlight: inFlight });
    try {
      await api.post(`/api/admin/matching/${id}/confirm-duplicate`);
      const { rows, stats, pagination } = get();
      const nextExpanded = nextRowIdAfter(rows, id);
      const remaining = rows.filter((r) => r.id !== id);
      set({
        rows: remaining,
        expandedRowId: nextExpanded,
        stats: { possibleDuplicate: Math.max(0, stats.possibleDuplicate - 1) },
        pagination: { ...pagination, total: Math.max(0, pagination.total - 1) },
      });
      if (remaining.length === 0) {
        const newTotal = Math.max(0, pagination.total - 1);
        if (newTotal > 0) {
          const newTotalPages = Math.max(1, Math.ceil(newTotal / pagination.pageSize));
          const targetPage = Math.min(pagination.page, newTotalPages);
          await get().fetchQueue(targetPage);
        }
      }
    } finally {
      const after = new Set(get().actionInFlight);
      after.delete(id);
      set({ actionInFlight: after });
    }
  },

  skip: (id) => {
    const { rows } = get();
    set({ expandedRowId: nextRowIdAfter(rows, id) });
  },
}));

export type { MatchingSort };
